import { Link } from "react-router-dom";
import logo from "@/assets/tcs-logo.png";

type FooterLink = {
  label: string;
  href: string;
  external?: boolean;
};

type FooterLinks = {
  services: FooterLink[];
  industries: FooterLink[];
  resources: FooterLink[];
  company: FooterLink[];
  legal: FooterLink[];
};

const footerLinks: FooterLinks = {
  services: [
    { label: "AI SEO Agency", href: "/services/geo/ai-seo-agency" },
    { label: "GEO Services", href: "/services/geo" },
    { label: "AI Paid Ads", href: "/services/ai-ads" },
    { label: "AI Content & SEO", href: "/services/ai-content" },
    { label: "AI Agents", href: "/services/ai-agents" },
  ],
  industries: [
    { label: "All Industries", href: "/industries" },
    { label: "SaaS Companies", href: "/industries/seo-saas-companies" },
    { label: "B2B Businesses", href: "/industries/seo-b2b-companies" },
    { label: "Enterprise", href: "/industries/ai-agents-enterprise" },
  ],
  resources: [
    { label: "Insights", href: "/insights" },
    { label: "Resources", href: "/resources" },
    { label: "Case Studies", href: "/case-studies" },
    { label: "AI Growth Audit", href: "/tools/ai-growth-audit" },
    { label: "ROI Calculator", href: "/roi-calculator" },
  ],
  company: [
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
    { label: "Pricing", href: "/pricing" },
    { label: "FAQs", href: "/faqs" },
  ],
  legal: [
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms of Service", href: "/terms" },
  ],
};

export function Footer() {
  return (
    <footer className="border-t border-border bg-secondary/30">
      <div className="container-wide py-16 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 md:gap-12">
          {/* Logo & Description */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/">
              <img src={logo} alt="The Cloud SEO" className="h-8 w-auto mb-4 dark:invert dark:brightness-200" />
            </Link>
            <p className="text-sm text-muted-foreground max-w-xs">
              AI-powered marketing for B2B leaders. Transform your growth with intelligent automation.
            </p>
            <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
              <span>🇺🇸</span>
              <span>🇮🇳</span>
              <span>🇦🇺</span>
              <span>🇦🇪</span>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold mb-4">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h4 className="font-semibold mb-4">Industries</h4>
            <ul className="space-y-3">
              {footerLinks.industries.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-semibold mb-4">Resources</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.href + link.label}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} The Cloud SEO. All rights reserved.
          </p>
          <p className="text-sm text-muted-foreground">AI-First Marketing Agency</p>
        </div>
      </div>
    </footer>
  );
}
